import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useDisplayError } from '@app/shared/hooks/useDisplayError';
import { PriceType } from '@app/shared/types/common';
import { cn } from '@app/shared/utils/common';
import { BalanceHistoryList } from '@app/shared/ui/organisms/BalanceHistory';
import { useSearchParamsUpdater } from '@app/shared/hooks/useSearchParamsUpdater';
import { getFiltersFromBalanceHistoryType } from '@app/shared/utils/balance-history';
import { useGetBalanceHistoryQuery } from '@app/store/slices/balance/balanceApi';
import { BalanceHistoryRequest } from '@app/store/slices/balance/balanceTypes';

export default function BalanceHistory() {
  const { t } = useTranslation();

  const { searchParams, updateSearchParams } = useSearchParamsUpdater<BalanceHistoryRequest>();
  const type = searchParams.get('type') as PriceType | null;
  const balanceHistoryFilters = useMemo(
    (): BalanceHistoryRequest => ({
      type: type ?? undefined,
    }),
    [type]
  );

  const handleBalanceTypeByChange = (value: PriceType | null) => {
    const filters = getFiltersFromBalanceHistoryType(value);
    updateSearchParams({
      type: filters.type ?? undefined,
    });
  };

  const {
    data: balanceHistory,
    isLoading: isBalanceHistoryLoading,
    isError: isBalanceHistoryError,
    refetch: refetchBalanceHistory,
  } = useGetBalanceHistoryQuery(balanceHistoryFilters);

  useDisplayError(isBalanceHistoryError, t('errors.failed_to_load_balance_history'));

  return (
    <BalanceHistoryList
      rows={balanceHistory}
      isBalanceHistoryLoading={isBalanceHistoryLoading}
      refetch={refetchBalanceHistory}
      type={type}
      handleBalanceTypeByChange={handleBalanceTypeByChange}
      className={cn('h-full overflow-auto', 'scrollbar-hide')}
    />
  );
}
